import React from 'react';

const StatusBadge = ({ 
  status, 
  variant = 'default',
  colorMap,
  className = '',
  ...props 
}) => {
  // Default color mappings for different variants
  const defaultColorMaps = {
    device: {
      active: 'bg-green-100 text-green-800',
      blocked: 'bg-red-100 text-red-800',
      lost: 'bg-yellow-100 text-yellow-800',
      retired: 'bg-gray-100 text-gray-800',
      donated: 'bg-amber-100 text-amber-800',
      wasted: 'bg-red-100 text-red-800',
      draft: 'bg-slate-100 text-slate-800',
      demo: 'bg-blue-100 text-blue-800',
      sponsored: 'bg-purple-100 text-purple-800',
      print_label: 'bg-cyan-100 text-cyan-800',
      // Legacy support
      inactive: 'bg-red-100 text-red-800',
      maintenance: 'bg-yellow-100 text-yellow-800'
    },
    repair: {
      draft: 'bg-gray-100 text-gray-800',
      backlog: 'bg-slate-100 text-slate-800',
      waiting_for_repair: 'bg-yellow-100 text-yellow-800',
      clarification_required: 'bg-orange-100 text-orange-800',
      // Legacy alias for backward compatibility
      waiting_for_clarification: 'bg-orange-100 text-orange-800',
      waiting_for_dev_input: 'bg-cyan-100 text-cyan-800',
      waiting_for_customer_response: 'bg-amber-100 text-amber-800',
      work_in_progress: 'bg-blue-100 text-blue-800',
      repaired: 'bg-green-100 text-green-800',
      ready_for_shipping: 'bg-purple-100 text-purple-800',
      waiting_for_payment: 'bg-red-100 text-red-800',
      completed: 'bg-gray-100 text-gray-800',
      // Legacy statuses for backward compatibility
      in_progress: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800',
      received: 'bg-yellow-100 text-yellow-800',
      clarification: 'bg-orange-100 text-orange-800',
      waiting_spare_parts: 'bg-slate-100 text-slate-800',
      waiting_repair: 'bg-yellow-100 text-yellow-800',
      waiting_payment: 'bg-red-100 text-red-800',
      ready4shipment: 'bg-purple-100 text-purple-800',
      pending: 'bg-slate-100 text-slate-800',
      wip: 'bg-blue-100 text-blue-800',
      done: 'bg-gray-100 text-gray-800'
    },
    priority: {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-red-100 text-red-800',
      urgent: 'bg-red-100 text-red-800'
    },
    role: {
      admin: 'bg-red-100 text-red-800',
      accounting: 'bg-emerald-100 text-emerald-800',
      technician: 'bg-blue-100 text-blue-800',
      user: 'bg-gray-100 text-gray-800',
      device_agent: 'bg-purple-100 text-purple-800'
    },
    type: {
      paid: 'bg-green-100 text-green-800',
      warranty: 'bg-purple-100 text-purple-800',
      internal: 'bg-blue-100 text-blue-800',
      // Legacy
      free: 'bg-blue-100 text-blue-800'
    },
    default: {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      draft: 'bg-gray-100 text-gray-800'
    }
  };

  // Display name mappings for different variants
  const displayNameMaps = {
    repair: {
      draft: 'Draft',
      backlog: 'Backlog',
      waiting_for_repair: 'Waiting for repair',
      clarification_required: 'Clarification required',
      // Legacy alias for backward compatibility
      waiting_for_clarification: 'Clarification required',
      waiting_for_dev_input: 'Waiting for dev input',
      waiting_for_customer_response: 'Waiting for customer response',
      work_in_progress: 'Work in progress',
      repaired: 'Repaired',
      ready_for_shipping: 'Ready for shipping',
      waiting_for_payment: 'Waiting for payment',
      completed: 'Completed',
      // Legacy statuses
      in_progress: 'Work in progress',
      cancelled: 'Cancelled',
      received: 'Received',
      clarification: 'Waiting for clarification',
      waiting_repair: 'Waiting for repair',
      waiting_payment: 'Waiting for payment',
      waiting_spare_parts: 'Backlog',
      ready4shipment: 'Ready for shipping',
      pending: 'Backlog',
      wip: 'Work in progress',
      done: 'Completed'
    },
    priority: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent'
    },
    type: {
      paid: 'Paid',
      warranty: 'Warranty',
      internal: 'Internal',
      free: 'Internal' // Legacy
    },
    device: {
      active: 'Active',
      blocked: 'Blocked',
      lost: 'Lost',
      retired: 'Retired',
      donated: 'Donated',
      wasted: 'Trashed',
      draft: 'Draft',
      demo: 'Demo',
      sponsored: 'Sponsored',
      print_label: 'Print label',
      inactive: 'Inactive',
      maintenance: 'Maintenance'
    },
    role: {
      admin: 'Admin',
      accounting: 'Accounting',
      technician: 'Technician',
      user: 'User',
      device_agent: 'Device Agent'
    }
  };

  // Use provided colorMap or fall back to variant-specific or default
  const colors = colorMap || defaultColorMaps[variant] || defaultColorMaps.default || {};
  const colorClass = colors[status] || 'bg-gray-100 text-gray-800';
  
  // Get display name or fall back to status
  const displayNames = displayNameMaps[variant] || {};
  const displayName = displayNames[status] || status;

  return (
    <span 
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colorClass} ${className}`}
      {...props}
    >
      {displayName}
    </span>
  );
};

export default StatusBadge;
